import { Navigate, Route, Routes } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useEffect } from "react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import LoginScreen from "./screens/auth/LoginScreen";
import RegisterScreen from "./screens/auth/RegisterScreen";
import ForgotPasswordScreen from "./screens/auth/ForgotPasswordScreen";
import CalendarScreen from "./screens/calendar/CalendarScreen";
import PropertiesScreen from "./screens/properties/PropertiesScreen";
import StatisticsScreen from "./screens/statistics/StatisticsScreen";
import ProfileScreen from "./screens/profile/ProfileScreen";
import CleanersScreen from "./screens/cleaners/CleanersScreen";
import CleaningTasksScreen from "./screens/cleaning/CleaningTasksScreen";
import InvoicesScreen from "./screens/invoices/InvoicesScreen";
import SubscriptionScreen from "./screens/subscription/SubscriptionScreen";
import SupportScreen from "./screens/support/SupportScreen";
import ReservationsScreen from "./screens/reservations/ReservationsScreen";
import GuestsScreen from "./screens/guests/GuestsScreen";
import { AppLayout } from "./components/layout/AppLayout";
import { RouteNames } from "./navigation/RouteNames";
import {
  loadCurrentUser,
  markSubscriptionExpired,
} from "./store/slices/authSlice";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import AppMessageModal from "./components/modals/AppMessageModal";
import {
  canManageBilling,
  canManageOperations,
  canManageTeamMembers,
  canViewCalendar,
  canViewCleaningTasks,
  canViewInvoices,
  isCleaner,
} from "./utils/helperFunctions/roleHelper";
import ResetPasswordScreen from "./screens/auth/ResetPasswordScreen";
import { showMessage } from "./store/slices/appMessageSlice";
import { setSubscriptionExpiredHandler } from "./api/apiClient";
import { useTranslation } from "./translations/i18n";
import { isCustomSubscription } from "./utils/helperFunctions/subscriptionAccessHelper";

const googleClientId = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID || "";

const FullScreenLoader = () => {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CircularProgress />
    </Box>
  );
};

const ProtectedRoute = () => {
  const { token, initialized } = useAppSelector((state) => state.auth);

  if (!initialized) {
    return <FullScreenLoader />;
  }

  if (!token) {
    return <Navigate to={RouteNames.login} replace />;
  }

  return <AppLayout />;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { token, user } = useAppSelector((state) => state.auth);

  if (token) {
    return (
      <Navigate
        to={
          isCleaner(user?.role) ? RouteNames.cleaningTasks : RouteNames.calendar
        }
        replace
      />
    );
  }

  return <>{children}</>;
};

const CalendarRoute = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAppSelector((state) => state.auth);

  if (!canViewCalendar(user?.role)) {
    return (
      <Navigate
        to={isCleaner(user?.role) ? RouteNames.cleaningTasks : RouteNames.menu}
        replace
      />
    );
  }

  return <>{children}</>;
};

const OperationRoute = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAppSelector((state) => state.auth);

  if (!canManageOperations(user?.role)) {
    return <Navigate to={RouteNames.calendar} replace />;
  }

  return <>{children}</>;
};

const TeamRoute = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAppSelector((state) => state.auth);

  if (!canManageTeamMembers(user?.role)) {
    return <Navigate to={RouteNames.calendar} replace />;
  }

  return <>{children}</>;
};

const CleaningRoute = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAppSelector((state) => state.auth);

  if (!canViewCleaningTasks(user?.role)) {
    return <Navigate to={RouteNames.calendar} replace />;
  }

  return <>{children}</>;
};

const InvoiceRoute = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAppSelector((state) => state.auth);

  if (!canViewInvoices(user?.role)) {
    return <Navigate to={RouteNames.calendar} replace />;
  }

  return <>{children}</>;
};

const BillingRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, account } = useAppSelector((state) => state.auth);

  if (!canManageBilling(user?.role) || isCustomSubscription(account)) {
    return <Navigate to={RouteNames.calendar} replace />;
  }

  return <>{children}</>;
};
const DefaultRoute = () => {
  const { token, initialized, user } = useAppSelector((state) => state.auth);

  if (!initialized) {
    return <FullScreenLoader />;
  }

  if (!token) {
    return <Navigate to={RouteNames.login} replace />;
  }

  return (
    <Navigate
      to={
        isCleaner(user?.role) ? RouteNames.cleaningTasks : RouteNames.calendar
      }
      replace
    />
  );
};

const AppRoutes = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { account } = useAppSelector((state) => state.auth);

  useEffect(() => {
    dispatch(loadCurrentUser());
  }, [dispatch]);

  useEffect(() => {
    setSubscriptionExpiredHandler(() => {
      dispatch(markSubscriptionExpired());

      dispatch(
        showMessage({
          type: "error",

          title: t("subscription.accessExpiredTitle"),

          message: t(
            isCustomSubscription(account)
              ? "subscription.customAccessExpiredMessage"
              : "subscription.accessExpiredMessage",
          ),
        }),
      );
    });

    return () => {
      setSubscriptionExpiredHandler(null);
    };
  }, [account, dispatch, t]);

  return (
    <>
      <Routes>
        <Route
          path={RouteNames.login}
          element={
            <PublicRoute>
              <LoginScreen />
            </PublicRoute>
          }
        />

        <Route
          path={RouteNames.register}
          element={
            <PublicRoute>
              <RegisterScreen />
            </PublicRoute>
          }
        />

        <Route
          path={RouteNames.forgotPassword}
          element={
            <PublicRoute>
              <ForgotPasswordScreen />
            </PublicRoute>
          }
        />

        <Route
          path={RouteNames.resetPassword}
          element={<ResetPasswordScreen />}
        />

        <Route element={<ProtectedRoute />}>
          <Route
            path={RouteNames.calendar}
            element={
              <CalendarRoute>
                <CalendarScreen />
              </CalendarRoute>
            }
          />

          <Route
            path={RouteNames.properties}
            element={
              <OperationRoute>
                <PropertiesScreen />
              </OperationRoute>
            }
          />

          <Route
            path={RouteNames.reservations}
            element={
              <OperationRoute>
                <ReservationsScreen />
              </OperationRoute>
            }
          />

          <Route
            path={RouteNames.guests}
            element={
              <OperationRoute>
                <GuestsScreen />
              </OperationRoute>
            }
          />

          <Route
            path={RouteNames.statistics}
            element={
              <OperationRoute>
                <StatisticsScreen />
              </OperationRoute>
            }
          />

          <Route
            path={RouteNames.team}
            element={
              <TeamRoute>
                <CleanersScreen />
              </TeamRoute>
            }
          />

          <Route
            path={RouteNames.cleaningTasks}
            element={
              <CleaningRoute>
                <CleaningTasksScreen />
              </CleaningRoute>
            }
          />

          <Route
            path={RouteNames.invoices}
            element={
              <InvoiceRoute>
                <InvoicesScreen />
              </InvoiceRoute>
            }
          />

          <Route
            path={RouteNames.subscription}
            element={
              <BillingRoute>
                <SubscriptionScreen />
              </BillingRoute>
            }
          />

          <Route path={RouteNames.menu} element={<ProfileScreen />} />
          <Route
            path={RouteNames.profile}
            element={<Navigate to={RouteNames.menu} replace />}
          />
          <Route path={RouteNames.support} element={<SupportScreen />} />
        </Route>

        <Route path="*" element={<DefaultRoute />} />
      </Routes>

      <AppMessageModal />
    </>
  );
};

export const App = () => {
  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <AppRoutes />
    </GoogleOAuthProvider>
  );
};

export default App;
